// =============================================================================
// iTravel 2.0 — Auth Hooks (TanStack Query)
// =============================================================================
// Custom hooks that wrap auth API calls with TanStack Query mutations/queries.
// These hooks handle the full auth lifecycle including updating the Zustand
// auth store on success, clearing state on logout, and error handling.
//
// Hooks:
//   useLogin()           — Mutation for email/password authentication
//   useRegister()        — Mutation for account creation
//   useLogout()          — Mutation for logging out (token revocation)
//   useForgotPassword()  — Mutation for requesting a password reset email
//   useResetPassword()   — Mutation for resetting password with a token
//   useChangePassword()  — Mutation for changing password (authenticated)
//   useCurrentUser()     — Query for fetching the authenticated user profile
//   useHydrateAuth()     — Effect hook for initial auth hydration on app start
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type {
  ForgotPasswordPayload,
  LoginPayload,
  RegisterPayload,
  ResetPasswordPayload,
} from '@itravel/shared';

import { authApi } from '@/features/auth/api/auth.api';
import { useAuthStore } from '@/store/auth.store';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
} as const;

// ---------------------------------------------------------------------------
// useLogin
// ---------------------------------------------------------------------------

interface UseLoginOptions {
  /** Called after a successful login (e.g., to navigate) */
  onSuccess?: () => void;
  /** Called when the login fails */
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for authenticating with email and password.
 *
 * On success:
 * - Stores user and tokens in the auth store
 * - Invalidates the `auth/me` query cache
 * - Navigates to the `returnTo` URL if present, otherwise to `/`
 *
 * Usage:
 * ```tsx
 * const { mutate: login, isPending } = useLogin();
 * login({ email, password });
 * ```
 */
export function useLogin(options?: UseLoginOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setLoading, setAuthenticated, setError, clearError } = useAuthStore.getState();

  return useMutation({
    mutationFn: (payload: LoginPayload) => {
      setLoading();
      clearError();
      return authApi.login(payload);
    },
    onSuccess: (data) => {
      setAuthenticated(data.user, data.tokens);
      queryClient.setQueryData(authKeys.me(), data.user);

      options?.onSuccess?.();

      // Navigate to returnTo if present, otherwise home
      const returnTo = searchParams.get('returnTo');
      navigate(returnTo ?? '/', { replace: true });
    },
    onError: async (error: Error) => {
      const message = await extractErrorMessage(error, 'Invalid email or password');
      setError(message);
      options?.onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// useRegister
// ---------------------------------------------------------------------------

interface UseRegisterOptions {
  /** Called after successful registration */
  onSuccess?: () => void;
  /** Called when registration fails */
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for creating a new user account.
 *
 * On success:
 * - Stores user and tokens in the auth store (auto-login)
 * - Navigates to `/`
 *
 * Usage:
 * ```tsx
 * const { mutate: register, isPending } = useRegister();
 * register({ email, password, displayName });
 * ```
 */
export function useRegister(options?: UseRegisterOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setLoading, setAuthenticated, setError, clearError } = useAuthStore.getState();

  return useMutation({
    mutationFn: (payload: RegisterPayload) => {
      setLoading();
      clearError();
      return authApi.register(payload);
    },
    onSuccess: (data) => {
      setAuthenticated(data.user, data.tokens);
      queryClient.setQueryData(authKeys.me(), data.user);

      options?.onSuccess?.();
      navigate('/', { replace: true });
    },
    onError: async (error: Error) => {
      const message = await extractErrorMessage(error, 'Registration failed. Please try again.');
      setError(message);
      options?.onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// useLogout
// ---------------------------------------------------------------------------

interface UseLogoutOptions {
  /** Called after successful logout */
  onSuccess?: () => void;
}

/**
 * Mutation hook for logging out the current user.
 *
 * On success:
 * - Revokes refresh tokens on the server
 * - Clears the auth store
 * - Clears all query caches
 * - Navigates to `/login`
 *
 * Usage:
 * ```tsx
 * const { mutate: logout } = useLogout();
 * <Button onClick={() => logout()}>Log out</Button>
 * ```
 */
export function useLogout(options?: UseLogoutOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const store = useAuthStore.getState();

  return useMutation({
    mutationFn: async () => {
      // Before anything clears the session, because releasing the device is an
      // authenticated call. Doing it in the teardown that follows would send it
      // with no bearer token, and the phone would keep receiving this account's
      // notifications until someone else signed in on it.
      const { releaseDeviceFromPush } = await import('@/lib/push/push');
      await releaseDeviceFromPush();

      const refreshToken = store.getRefreshToken();
      return authApi.logout(refreshToken ?? undefined);
    },
    onSettled: () => {
      // Always clear local state, even if the server call fails
      // (e.g., due to network issues or already-revoked token)
      store.logout();
      // reset rather than clear: clear() detaches any screen still mounted from
      // its query, so a fetch in flight never resolves and never retries. Both
      // drop the previous account's data; only one leaves the app able to
      // recover if a render happens before the redirect.
      void queryClient.resetQueries();

      options?.onSuccess?.();
      navigate('/login', { replace: true });
    },
  });
}

// ---------------------------------------------------------------------------
// useForgotPassword
// ---------------------------------------------------------------------------

interface UseForgotPasswordOptions {
  /** Called after the request is sent (always succeeds to prevent email enumeration) */
  onSuccess?: (message: string) => void;
  /** Called if the request fails (network error, etc.) */
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for requesting a password reset email.
 *
 * Usage:
 * ```tsx
 * const { mutate: forgotPassword, isPending, isSuccess } = useForgotPassword();
 * forgotPassword({ email });
 * ```
 */
export function useForgotPassword(options?: UseForgotPasswordOptions) {
  return useMutation({
    mutationFn: (payload: ForgotPasswordPayload) => authApi.forgotPassword(payload),
    onSuccess: (message) => {
      options?.onSuccess?.(message);
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// useResetPassword
// ---------------------------------------------------------------------------

interface UseResetPasswordOptions {
  /** Called after password is successfully reset */
  onSuccess?: (message: string) => void;
  /** Called if the reset fails (invalid/expired token, etc.) */
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for resetting a password with a token from email.
 *
 * On success, the user should be directed to the login page to sign in
 * with their new password.
 *
 * Usage:
 * ```tsx
 * const { mutate: resetPassword, isPending } = useResetPassword();
 * resetPassword({ token, password });
 * ```
 */
export function useResetPassword(options?: UseResetPasswordOptions) {
  return useMutation({
    mutationFn: (payload: ResetPasswordPayload) => authApi.resetPassword(payload),
    onSuccess: (message) => {
      options?.onSuccess?.(message);
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// useChangePassword
// ---------------------------------------------------------------------------

interface UseChangePasswordOptions {
  /** Called after password is successfully changed */
  onSuccess?: (message: string) => void;
  /** Called if the change fails */
  onError?: (error: Error) => void;
}

interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

/**
 * Mutation hook for changing the password of the currently authenticated user.
 *
 * Usage:
 * ```tsx
 * const { mutate: changePassword, isPending } = useChangePassword();
 * changePassword({ currentPassword, newPassword });
 * ```
 */
export function useChangePassword(options?: UseChangePasswordOptions) {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: ChangePasswordPayload) =>
      authApi.changePassword(currentPassword, newPassword),
    onSuccess: (message) => {
      options?.onSuccess?.(message);
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// useCurrentUser
// ---------------------------------------------------------------------------

/**
 * Query hook for fetching the currently authenticated user's profile.
 *
 * Only runs when the user is authenticated (has a valid access token).
 * The query is seeded from the auth store so it's immediately available
 * after login/register without an additional network request.
 *
 * Usage:
 * ```tsx
 * const { data: user, isLoading } = useCurrentUser();
 * ```
 */
export function useCurrentUser() {
  const isAuthenticated = useAuthStore((s) => s.status === 'authenticated');
  const storeUser = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: authApi.getMe,
    enabled: isAuthenticated,
    // Use the stored user as initial/placeholder data so the UI doesn't flash
    initialData: storeUser ?? undefined,
    staleTime: 1000 * 60 * 5, // Consider fresh for 5 minutes
  });
}

// ---------------------------------------------------------------------------
// useHydrateAuth
// ---------------------------------------------------------------------------

/**
 * Effect hook that runs on app startup to validate persisted auth state.
 *
 * If a refresh token exists in the persisted store, it attempts to:
 * 1. Fetch the current user profile from the server
 * 2. On success: confirm the auth state as valid (reading CURRENT store
 *    tokens, which may have been rotated by the API interceptor)
 * 3. On auth failure (401/403): clear the persisted auth state
 * 4. On network failure: keep persisted state and allow offline usage
 *
 * IMPORTANT: After `getMe()` returns, we read the tokens from the store
 * via `getState()` — NOT from React state / closure variables. The API
 * interceptor may have silently refreshed (rotated) the tokens during
 * the request, and using stale closure values would overwrite the fresh
 * tokens with revoked ones, causing "token reuse detected" on the next
 * page reload.
 *
 * This should be called once at the app root level.
 *
 * Usage:
 * ```tsx
 * function App() {
 *   useHydrateAuth();
 *   return <RouterProvider router={router} />;
 * }
 * ```
 */
// Module-level flag to prevent React StrictMode double-mount from firing
// hydration twice. Without this, two concurrent getMe() calls race and can
// trigger unnecessary token rotations or hit the rate limiter.
let hydrationInFlight = false;

export function useHydrateAuth() {
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    if (status !== 'idle') return;
    if (hydrationInFlight) return;
    hydrationInFlight = true;

    const { setHydrated, setAuthenticated, logout: clearAuth } = useAuthStore.getState();

    async function hydrate() {
      // Read persisted credentials from the store at call time (not from
      // a React selector, which may not have updated yet).
      const { refreshToken, user } = useAuthStore.getState();

      // No persisted credentials — mark as hydrated and done
      if (!refreshToken || !user) {
        clearAuth();
        setHydrated();
        return;
      }

      // -------------------------------------------------------------------
      // Optimistic: trust the persisted session and let the app render NOW.
      //
      // The previous version awaited getMe() before releasing the guard, so
      // every cold start sat on "Checking authentication…" for a round trip —
      // on a slow connection, seconds of staring at a spinner while holding a
      // refresh token good for 30 days. The check still happens, just not in
      // front of the user.
      // -------------------------------------------------------------------
      const { accessToken: persistedAccess } = useAuthStore.getState();
      setAuthenticated(user, {
        accessToken: persistedAccess ?? '',
        refreshToken,
      });
      setHydrated();

      // -------------------------------------------------------------------
      // Verify in the background. Only a definitive rejection from the server
      // ends the session; anything else (offline, server down) leaves it be,
      // because being unreachable is not the same as being signed out.
      // -------------------------------------------------------------------
      try {
        const freshUser = await authApi.getMe();

        // Re-read tokens: the interceptor may have rotated them during the
        // call above, so a closure variable would be stale.
        const currentState = useAuthStore.getState();

        if (currentState.accessToken && currentState.refreshToken) {
          setAuthenticated(freshUser, {
            accessToken: currentState.accessToken,
            refreshToken: currentState.refreshToken,
          });
        }
      } catch (error: unknown) {
        if (isHttpAuthError(error)) {
          // The server rejected the token and it could not be refreshed —
          // this is the case that genuinely signs the user out.
          clearAuth();
        } else {
          console.warn(
            '[Pintravel] Background auth check failed due to a network/server ' +
              'error. Keeping the persisted session.',
            error,
          );
        }
      }
    }

    hydrate().finally(() => {
      hydrationInFlight = false;
    });
  }, [status]);
}

// ---------------------------------------------------------------------------
// Internal Helpers
// ---------------------------------------------------------------------------

/**
 * Checks whether an error represents an HTTP 401 or 403 response,
 * indicating the user's credentials/tokens are truly invalid (as opposed
 * to a network failure or server error).
 */
function isHttpAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  // ky HTTPError exposes a `response` property
  if ('response' in error && error.response instanceof Response) {
    const status = error.response.status;
    return status === 401 || status === 403;
  }

  // Fallback: check for status on the error object itself
  if ('status' in error && typeof (error as Record<string, unknown>).status === 'number') {
    const status = (error as Record<string, unknown>).status;
    return status === 401 || status === 403;
  }

  return false;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extracts a human-readable error message from a ky HTTP error or generic Error.
 *
 * Ky errors have a `response` property with JSON error details from our server.
 * This helper tries to parse that, falling back to the generic error message
 * or a provided fallback.
 */
async function extractErrorMessage(
  error: unknown,
  fallback = 'An unexpected error occurred',
): Promise<string> {
  // ky HTTPError has a response property
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response instanceof Response
  ) {
    try {
      const body = (await error.response.json()) as {
        data?: { message?: string };
        error?: { message?: string };
        message?: string;
      };

      return body?.error?.message ?? body?.data?.message ?? body?.message ?? fallback;
    } catch {
      // Response isn't JSON
    }
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  return fallback;
}
